// SPDX-License-Identifier: AGPL-3.0-or-later
// Per-device display preferences. They hold no financial data, so they live
// in this browser's storage rather than in the encrypted records.
import { useSyncExternalStore } from "react";
import type { Locale } from "@almanac/i18n";

export type Mode = "light" | "grey" | "dark" | "system";
export type PaletteId = "default" | "ocean" | "forest" | "colorblind" | "contrast" | "custom";

export interface Colors {
  positive: string;
  negative: string;
  budget: string;
  actual: string;
}

export interface Prefs {
  mode: Mode;
  palette: PaletteId;
  custom: Colors;
  locale: Locale;
  units: "dollars" | "percent";
  calendarView: "3d" | "2d";
  /** The dashboard's period: one month, or the previous, present and next month. */
  dashboardView: "month" | "outlook";
}

export const PALETTES: Record<Exclude<PaletteId, "custom">, Colors> = {
  default: { positive: "#0f9f8f", negative: "#e4576b", budget: "#0f766e", actual: "#f0795b" },
  ocean: { positive: "#2f7fd8", negative: "#e8822b", budget: "#1d5fae", actual: "#f39c42" },
  forest: { positive: "#2f9e44", negative: "#c2255c", budget: "#2b8a3e", actual: "#d6336c" },
  // Blue/orange pairs from the Okabe–Ito set stay distinct for most colour vision.
  colorblind: { positive: "#0072b2", negative: "#d55e00", budget: "#0072b2", actual: "#e69f00" },
  contrast: { positive: "#006400", negative: "#b00020", budget: "#000080", actual: "#a04000" },
};

const KEY = "almanac.prefs.v1";

function detectLocale(): Locale {
  return typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("fr") ? "fr-CA" : "en-CA";
}

const defaults: Prefs = {
  mode: "system",
  palette: "default",
  custom: { ...PALETTES.default },
  locale: detectLocale(),
  units: "dollars",
  calendarView: "3d",
  dashboardView: "month",
};

function read(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...defaults, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    // Storage can be unavailable (private windows); defaults are fine.
  }
  return defaults;
}

let current = read();
const listeners = new Set<() => void>();

export function getPrefs(): Prefs {
  return current;
}

export function setPrefs(changes: Partial<Prefs>) {
  current = { ...current, ...changes };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    // Not fatal: the change still applies for this visit.
  }
  listeners.forEach((l) => l());
}

export function usePrefs(): Prefs {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getPrefs,
  );
}

export function colorsFor(prefs: Prefs): Colors {
  return prefs.palette === "custom" ? prefs.custom : PALETTES[prefs.palette];
}

/** The colour opposite on the colour wheel, used to pair a custom budget colour with its actual colour. */
export function complementary(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const sumRgb = max + min;
  const out = [r, g, b].map((c) => sumRgb - c);
  return `#${out.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/** Applies mode and palette to the page as CSS variables. */
export function applyTheme(prefs: Prefs) {
  const root = document.documentElement;
  const dark = typeof matchMedia !== "undefined" && matchMedia("(prefers-color-scheme: dark)").matches;
  const mode = prefs.mode === "system" ? (dark ? "dark" : "light") : prefs.mode;
  root.dataset.mode = mode;
  root.dataset.palette = prefs.palette;
  root.lang = prefs.locale;
  const colors = colorsFor(prefs);
  root.style.setProperty("--positive", colors.positive);
  root.style.setProperty("--negative", colors.negative);
  root.style.setProperty("--budget", colors.budget);
  root.style.setProperty("--actual", colors.actual);
}
